import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance, { api } from "./Pages/axiosInstance";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    console.log('AuthProvider rendering');
    // Core auth state
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    // Enhanced state tracking
    const [authState, setAuthState] = useState({
        status: 'initializing',
        lastCheck: null,
        loginAttempts: 0,
        maxLoginAttempts: 3,
        lockoutUntil: null
    });

    // Session tracking
    const [sessionState, setSessionState] = useState({
        startTime: null,
        lastActivity: null,
        isActive: false,
        deviceInfo: null
    });

    const navigate = useNavigate();

    // Initialize auth state
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setAuthState(prev => ({ ...prev, status: 'checking' }));
                await checkAuth();
                setAuthState(prev => ({ 
                    ...prev, 
                    status: 'ready',
                    lastCheck: Date.now()
                }));
            } catch (error) {
                console.error('Auth initialization error:', error);
                setAuthState(prev => ({ 
                    ...prev, 
                    status: 'error',
                    lastCheck: Date.now()
                }));
            }
        };

        initializeAuth();
    }, []);

    // Activity monitoring
    useEffect(() => {
        const updateActivity = () => {
            setSessionState(prev => ({
                ...prev,
                lastActivity: Date.now(),
                isActive: true
            }));
        };

        // Set up activity listeners
        ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        // Periodic session check
        const sessionCheck = setInterval(() => {
            const now = Date.now();
            const inactiveTime = now - sessionState.lastActivity;
            
            if (inactiveTime > 30 * 60 * 1000) { // 30 minutes
                handleInactivity();
            }
        }, 60000); // Check every minute

        return () => {
            ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
            clearInterval(sessionCheck);
        };
    }, [sessionState.lastActivity]);

    // Check authentication status
    const checkAuth = async () => {
        console.log('Starting auth check');
    const token = localStorage.getItem('authToken');
    console.log('Token exists:', !!token);  // Logs true/false without exposing token

    if (!token) {
        console.log('No token found, redirecting to login');
        handleUnauthenticated();
        setIsLoading(false);
        return;
    }

    try {
        console.log('Verifying token with server...');
        const response = await axiosInstance.get('/api/auth/user/info');
        console.log('Server response:', response.status);
        
        if (response.data) {
            console.log('Authentication successful');
            setUser(response.data);
            setIsAuthenticated(true);
            setError(null);
        }
    } catch (error) {
        console.error('Auth check failed:', {
            status: error.response?.status,
            message: error.message
        });
        handleUnauthenticated();
    } finally {
        console.log('Auth check complete, setting loading false');
        setIsLoading(false);
    }
};

// Authentication handlers
const handleAuthenticationSuccess = (userData, token) => {
  try {
      // Update local storage
      localStorage.setItem('authToken', token);
      if (userData?.role) {
          localStorage.setItem('userRole', userData.role);
      }
      if (userData?.userId) {
          localStorage.setItem('userId', userData.userId);
      }

      // Update session state
      setSessionState(prev => ({
          ...prev,
          startTime: Date.now(),
          lastActivity: Date.now(),
          isActive: true,
          deviceInfo: {
              type: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
              browser: navigator.userAgent,
              platform: navigator.platform
          }
      }));

      // Update auth state
      setAuthState(prev => ({
          ...prev,
          status: 'authenticated',
          lastCheck: Date.now(),
          loginAttempts: 0,
          lockoutUntil: null
      }));

      // Update axios default headers
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(userData);
      setIsAuthenticated(true);
      setError(null);
  } catch (error) {
      console.error('Error in authentication success handler:', error);
      handleAuthenticationError(error);
  }
};

const handleAuthenticationError = (error) => {
  console.error('Authentication error:', error);

  // Update auth state
  setAuthState(prev => ({
      ...prev,
      status: 'error',
      lastCheck: Date.now(),
      loginAttempts: prev.loginAttempts + 1
  }));

  // Check for lockout
  if (authState.loginAttempts >= authState.maxLoginAttempts) {
      const lockoutTime = Date.now() + (15 * 60 * 1000); // 15 minutes
      setAuthState(prev => ({
          ...prev,
          lockoutUntil: lockoutTime
      }));
  }

  // Handle different error types
  if (error.response?.status === 401) {
      handleUnauthenticated();
  } else {
      setError(error.response?.data?.message || 'Authentication error occurred');
  }
};

const handleUnauthenticated = () => {
  // Clear all auth data
  localStorage.removeItem('authToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userId');

  // Reset states
  setUser(null);
  setIsAuthenticated(false);
  delete axiosInstance.defaults.headers.common['Authorization'];

  // Update session state
  setSessionState({
      startTime: null,
      lastActivity: null,
      isActive: false,
      deviceInfo: null
  });

  // Navigate to login
  navigate('/login', { replace: true });
};

const handleInactivity = () => {
    console.log('User inactive, logging out...');
    logout(); // Just call logout directly instead of trying set-unavailable first
};

// Login handler
const login = async (token, userData) => {
  try {
      // Check for lockout
      if (authState.lockoutUntil && Date.now() < authState.lockoutUntil) {
          const remainingTime = Math.ceil((authState.lockoutUntil - Date.now()) / 1000 / 60);
          throw new Error(`Account temporarily locked. Try again in ${remainingTime} minutes.`);
      }

      // Get device info
      const deviceInfo = {
          type: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser: navigator.userAgent,
          platform: navigator.platform,
          timestamp: Date.now()
      };

      // Update user's online status
      await axiosInstance.post('/api/auth/user/set-active', { deviceInfo });

      handleAuthenticationSuccess(userData, token);
      navigate('/', { replace: true });
      return true;
  } catch (err) {
      console.error('Login error:', err);
      handleAuthenticationError(err);
      return false;
  }
};

// Logout handler
const logout = async () => {
  try {
      // Clean up socket connections if any
      if (window.socket) {
          window.socket.disconnect();
      }

      // Clean up audio context if any
      if (window.audioContext) {
          await window.audioContext.close();
      }

      // Update user's status on server
      try {
          await axiosInstance.post('/api/auth/user/set-unavailable');
      } catch (error) {
          console.error('Error updating user status:', error);
      }

      handleUnauthenticated();
  } catch (err) {
      console.error('Logout error:', err);
      // Force logout even if there's an error
      handleUnauthenticated();
  }
};

// User state management
const updateUser = async (userData) => {
  try {
      const response = await axiosInstance.put(api.auth.updateInfo, userData);
      if (response.data.user) {
          setUser(prevUser => ({
              ...prevUser,
              ...response.data.user,
          }));
          
          if (response.data.user.role) {
              localStorage.setItem('userRole', response.data.user.role);
          }

          // Update session state
          setSessionState(prev => ({
              ...prev,
              lastActivity: Date.now()
          }));

          return true;
      }
      return false;
  } catch (err) {
      console.error('Update user error:', err);
      if (err.response?.status === 401) {
          handleAuthenticationError(err);
      }
      return false;
  }
};

// Session management
const refreshSession = async () => {
  try {
      const token = localStorage.getItem('authToken');
      if (!token) {
          throw new Error('No token found');
      }

      const response = await axiosInstance.post('/api/auth/refresh-token', {
          deviceInfo: sessionState.deviceInfo
      });

      if (response.data.token) {
          handleAuthenticationSuccess(response.data.user, response.data.token);
          return true;
      }
      return false;
  } catch (error) {
      console.error('Session refresh error:', error);
      handleAuthenticationError(error);
      return false;
  }
};

// Error handling
const clearError = () => {
  setError(null);
  setAuthState(prev => ({
      ...prev,
      status: isAuthenticated ? 'authenticated' : 'unauthenticated'
  }));
};

// Context value
const value = {
  isAuthenticated,
  isLoading,
  user,
  error,
  authState,
  sessionState,
  login,
  logout,
  updateUser,
  refreshSession,
  clearError,
  checkAuth
};

// Loading state
if (isLoading) {
  return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-4 text-xl font-semibold text-gray-700">
                  Loading TalkConnect...
              </p>
          </div>
      </div>
  );
}

// Error boundary for auth errors
if (authState.status === 'error' && !isLoading) {
  return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
              <div className="text-red-500 text-xl mb-4">
                  <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
              </div>
              <p className="text-xl font-semibold text-gray-700 mb-4">
                  Authentication Error
              </p>
              <p className="text-gray-500 mb-4">
                  {error || 'There was a problem authenticating your account.'}
              </p>
              <button
                  onClick={checkAuth}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                  Retry
              </button>
          </div>
      </div>
  );
}

// Session expiry warning
const SessionWarning = () => {
  if (!isAuthenticated || !sessionState.startTime) return null;

  const sessionAge = Date.now() - sessionState.startTime;
  const showWarning = sessionAge > (23 * 60 * 60 * 1000); // 23 hours

  if (!showWarning) return null;

  return (
      <div className="fixed bottom-4 right-4 bg-yellow-100 border-l-4 border-yellow-500 p-4 max-w-md">
          <div className="flex">
              <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
              </div>
              <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                      Your session will expire soon. Please save your work and refresh the page.
                  </p>
              </div>
          </div>
      </div>
  );
};

return (
  <AuthContext.Provider value={value}>
      {children}
      <SessionWarning />
  </AuthContext.Provider>
);
};

// Custom hook for using auth context
export const useAuth = () => {
const context = useContext(AuthContext);
if (!context) {
  throw new Error('useAuth must be used within an AuthProvider');
}
return context;
};

export default AuthContext;